/**
 * Breadcrumbs, and the route registry behind them.
 *
 * One registry describes how every screen hangs off its parent. From it come two
 * things that must never disagree:
 *
 *   * the breadcrumb trail on a detail page — including the back button, which
 *     always returns to the immediate parent rather than wherever the browser
 *     history happens to point;
 *   * which sidebar entry is lit (`navPathFor`), so standing on a worksheet
 *     lights "Worklist" and standing in a method form lights "Test catalogue".
 *
 * Pages only ever say what the *current* record is called; every ancestor label
 * and link is derived here. A page that needs a dynamic ancestor label — adding
 * a sample under a request — passes it by pattern via `labels`.
 *
 * Static labels go through the terminology bundle where a key exists, so a
 * laboratory that renames "Test requests" renames its breadcrumbs too.
 */

import { ChevronRight, CornerUpLeft, Home } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { useI18n } from "@/i18n/I18nProvider";
import { toTitleCase } from "@/lib/format";

interface RouteDef {
  /** Route pattern as declared in App.tsx, e.g. "/requests/:id/samples/new". */
  pattern: string;
  /** Pattern of the parent screen. Omitted only for top-level screens. */
  parent?: string;
  /** Terminology key for the label; falls back to `label`. */
  labelKey?: string;
  /** Static label, or the fallback shown for a dynamic crumb before its record loads. */
  label?: string;
  /** Sidebar path to light. Defaults to the nearest ancestor that is a sidebar entry. */
  nav?: string;
}

const ROUTES: RouteDef[] = [
  { pattern: "/", labelKey: "nav.dashboard", label: "Dashboard" },
  { pattern: "/inbox", labelKey: "nav.inbox", label: "My tasks", parent: "/" },

  { pattern: "/requests", labelKey: "nav.requests", label: "Test requests", parent: "/" },
  { pattern: "/requests/new", labelKey: "action.new_request", label: "New test request", parent: "/requests" },
  { pattern: "/requests/deletions", labelKey: "nav.deletions", label: "Deletion requests", parent: "/requests" },
  { pattern: "/requests/:id", label: "Request", parent: "/requests" },
  { pattern: "/requests/:id/samples/new", label: "Add a sample", parent: "/requests/:id" },

  { pattern: "/reception", labelKey: "nav.reception", label: "Sample reception", parent: "/" },
  { pattern: "/reception/confirmations", labelKey: "nav.confirmations", label: "Sample confirmations", parent: "/reception" },
  { pattern: "/reception/handovers", labelKey: "nav.handovers", label: "Sample hand-over", parent: "/reception" },
  { pattern: "/reception/disposal", labelKey: "nav.disposal", label: "Sample disposal", parent: "/reception" },
  { pattern: "/reception/disposal/:id", label: "Disposal run", parent: "/reception/disposal" },

  { pattern: "/worklist", labelKey: "nav.worklist", label: "Worklist", parent: "/" },
  { pattern: "/worklist/assign", label: "Batch assignment", parent: "/worklist" },
  // The worksheet lives under /analyses, but the screen it belongs to is the worklist.
  { pattern: "/analyses/:id", label: "Worksheet", parent: "/worklist" },
  { pattern: "/worksheet-runs", labelKey: "nav.worksheet_runs", label: "Worksheets", parent: "/" },
  { pattern: "/worksheet-runs/:id", label: "Worksheet", parent: "/worksheet-runs" },
  { pattern: "/worksheet-runs/:id/samples/:testId", label: "Sample", parent: "/worksheet-runs/:id" },
  { pattern: "/bench-binder", labelKey: "nav.bench_binder", label: "Bench binder", parent: "/" },

  { pattern: "/outsourcing", labelKey: "nav.outsourcing", label: "Outsourced analyses", parent: "/" },

  { pattern: "/oversight", labelKey: "nav.oversight", label: "Centre performance", parent: "/" },

  { pattern: "/certificates", labelKey: "nav.certificates", label: "Certificates", parent: "/" },
  { pattern: "/certificates/:id", label: "Certificate", parent: "/certificates" },

  { pattern: "/equipment", labelKey: "nav.equipment", label: "Equipment", parent: "/" },
  { pattern: "/equipment/new", label: "Register an instrument", parent: "/equipment" },
  { pattern: "/equipment/:id/edit", label: "Instrument", parent: "/equipment" },
  { pattern: "/equipment/calibrations/:id", label: "Calibration", parent: "/equipment" },
  { pattern: "/equipment/:id", label: "Instrument", parent: "/equipment" },

  { pattern: "/monitoring", labelKey: "nav.monitoring", label: "Monitoring logs", parent: "/" },
  { pattern: "/monitoring/assignments", label: "Assigned logs", parent: "/monitoring" },
  { pattern: "/monitoring/entries/:id", label: "Log entry", parent: "/monitoring" },

  { pattern: "/inventory", labelKey: "nav.inventory", label: "Store & reagents", parent: "/" },
  { pattern: "/inventory/items/new", label: "Add a stock item", parent: "/inventory" },
  { pattern: "/inventory/items/:id", label: "Stock item", parent: "/inventory" },
  { pattern: "/inventory/items/:id/edit", label: "Stock item", parent: "/inventory/items/:id" },
  // A lot is addressed on its own so a recall can be linked to, so it hangs off
  // the store rather than off the item its URL does not carry.
  { pattern: "/inventory/lots/:id", label: "Lot", parent: "/inventory" },

  { pattern: "/instrument-data", labelKey: "nav.instruments", label: "Instrument data", parent: "/" },
  { pattern: "/instruments/connections", labelKey: "nav.connections", label: "Connections", parent: "/" },
  { pattern: "/instruments/connections/new", label: "Connect an instrument", parent: "/instruments/connections" },
  { pattern: "/instruments/connections/:id/edit", label: "Connection", parent: "/instruments/connections" },

  { pattern: "/catalog", labelKey: "nav.catalog", label: "Test catalogue", parent: "/" },
  { pattern: "/catalog/services/new", label: "Add an analysis service", parent: "/catalog" },
  { pattern: "/catalog/services/:id/edit", label: "Analysis service", parent: "/catalog" },
  { pattern: "/catalog/methods/new", label: "Add a method", parent: "/catalog" },
  { pattern: "/catalog/methods/:id/edit", label: "Method", parent: "/catalog" },
  { pattern: "/catalog/calculations/new", label: "New calculation", parent: "/catalog" },
  { pattern: "/catalog/calculations/:id", label: "Calculation", parent: "/catalog" },
  { pattern: "/catalog/profiles/new", label: "Add an analysis profile", parent: "/catalog" },
  { pattern: "/catalog/profiles/:id/edit", label: "Analysis profile", parent: "/catalog" },
  { pattern: "/catalog/sample-types/new", label: "Add a sample type", parent: "/catalog" },
  { pattern: "/catalog/sample-types/:id/edit", label: "Sample type", parent: "/catalog" },

  { pattern: "/catalog/external-labs", labelKey: "nav.external_labs", label: "External laboratories", parent: "/" },
  { pattern: "/catalog/external-labs/new", label: "Register a laboratory", parent: "/catalog/external-labs" },
  { pattern: "/catalog/external-labs/:id/edit", label: "Laboratory", parent: "/catalog/external-labs" },

  { pattern: "/documents", labelKey: "nav.documents", label: "Documents & SOPs", parent: "/" },
  { pattern: "/documents/new", label: "Register a document", parent: "/documents" },
  { pattern: "/documents/worksheets", labelKey: "nav.worksheet_templates", label: "Worksheet templates", parent: "/documents" },
  { pattern: "/documents/:id", label: "Document", parent: "/documents" },
  { pattern: "/documents/:id/edit", label: "Document", parent: "/documents/:id" },
  { pattern: "/documents/:id/preview/:revisionId", label: "Preview", parent: "/documents/:id" },
  { pattern: "/documents/:id/design/:revisionId", label: "Design", parent: "/documents/:id" },

  { pattern: "/validations", labelKey: "nav.validation", label: "Method validation", parent: "/" },
  { pattern: "/validations/new", label: "Plan a validation study", parent: "/validations" },
  { pattern: "/validations/:id", label: "Validation study", parent: "/validations" },

  { pattern: "/clients", labelKey: "nav.clients", label: "Clients", parent: "/" },
  { pattern: "/feedback", labelKey: "nav.feedback", label: "Customer feedback", parent: "/" },
  { pattern: "/feedback/:id", label: "Message", parent: "/feedback" },

  { pattern: "/admin/registers", label: "Registers", parent: "/" },

  { pattern: "/admin/departments", labelKey: "nav.departments", label: "Organisation", parent: "/" },
  { pattern: "/admin/departments/new", label: "Add a unit", parent: "/admin/departments" },
  { pattern: "/admin/departments/:id/edit", label: "Unit", parent: "/admin/departments" },
  // Sits in the administration section of the sidebar; its address is outside
  // /admin because the screen is everybody's and the notification links to it.
  { pattern: "/delegations", labelKey: "nav.delegations", label: "Delegated authority", parent: "/" },
  { pattern: "/admin/workflows", labelKey: "nav.workflows", label: "Workflows", parent: "/" },
  { pattern: "/admin/users", labelKey: "nav.users", label: "Users", parent: "/" },
  { pattern: "/admin/labels", labelKey: "nav.labels", label: "Terminology", parent: "/" },
  { pattern: "/admin/settings", labelKey: "nav.settings", label: "Settings", parent: "/" },
  { pattern: "/admin/backups", labelKey: "nav.backups", label: "Backup & restore", parent: "/" },

  // Global search reads as a filtered request register, so it lights the sidebar
  // entry the results belong to rather than nothing at all.
  { pattern: "/search", label: "Search results", parent: "/requests" },
  { pattern: "/profile", label: "Your account", parent: "/" },
];

const byPattern = new Map(ROUTES.map((route) => [route.pattern, route]));

function segments(path: string): string[] {
  return path.split("/").filter(Boolean);
}

/** Does `pattern` match the whole of `pathname`? `:params` match any one segment. */
function matches(pattern: string, pathname: string): boolean {
  const want = segments(pattern);
  const have = segments(pathname);
  if (want.length !== have.length) return false;
  return want.every((part, index) => part.startsWith(":") || part === have[index]);
}

function findRoute(pathname: string): RouteDef | null {
  // Literal patterns win over parameterised ones: /requests/new is "New test
  // request", not request ":id" with the value "new".
  let best: RouteDef | null = null;
  let bestLiterals = -1;
  for (const route of ROUTES) {
    if (!matches(route.pattern, pathname)) continue;
    const literals = segments(route.pattern).filter((part) => !part.startsWith(":")).length;
    if (literals > bestLiterals) {
      best = route;
      bestLiterals = literals;
    }
  }
  return best;
}

/** The `:param` values, by name, that `pathname` supplies for `pattern`. */
function paramsFor(pattern: string, pathname: string): Record<string, string> {
  const params: Record<string, string> = {};
  const have = segments(pathname);
  segments(pattern).forEach((part, index) => {
    if (part.startsWith(":")) params[part.slice(1)] = have[index]!;
  });
  return params;
}

/**
 * The concrete address of a crumb: the pattern with its `:params` filled in.
 *
 * Built from the pattern rather than by truncating the pathname, because an
 * ancestor is not always a prefix — the worksheet lives at /analyses/:id but
 * its parent is /worklist.
 */
function hrefFor(pattern: string, params: Record<string, string>): string {
  const parts = segments(pattern).map((part) =>
    part.startsWith(":") ? (params[part.slice(1)] ?? part) : part,
  );
  return parts.length === 0 ? "/" : "/" + parts.join("/");
}

/** The full ancestry, root first, ending with the matched route itself. */
function chainFor(pathname: string): RouteDef[] {
  const chain: RouteDef[] = [];
  let route = findRoute(pathname);
  while (route) {
    chain.unshift(route);
    route = route.parent ? (byPattern.get(route.parent) ?? null) : null;
  }
  return chain;
}

/**
 * Which sidebar entry the current address belongs to.
 *
 * Walks the ancestry until it meets a path that is actually in the sidebar, so
 * /analyses/:id lights "Worklist" and /catalog/methods/:id/edit lights "Test
 * catalogue". The dashboard at "/" is everyone's ancestor, so it only claims an
 * exact match — otherwise it would light on every screen.
 */
export function navPathFor(pathname: string, navPaths: ReadonlySet<string>): string | null {
  const chain = chainFor(pathname);
  if (chain.length === 0) return null;
  const params = paramsFor(chain[chain.length - 1]!.pattern, pathname);
  for (let index = chain.length - 1; index >= 0; index--) {
    const route = chain[index]!;
    if (route.nav && navPaths.has(route.nav)) return route.nav;
    const href = hrefFor(route.pattern, params);
    if (href === "/" && pathname !== "/") continue;
    if (navPaths.has(href)) return href;
  }
  return null;
}

export function Breadcrumbs({
  current,
  labels,
}: {
  /** What the record on this screen is called — a request code, a certificate number. */
  current?: ReactNode;
  /** Labels for dynamic *ancestors*, by pattern: `{ "/requests/:id": request.code }`. */
  labels?: Record<string, string>;
}) {
  const { pathname } = useLocation();
  const { t } = useI18n();

  const chain = chainFor(pathname);
  if (chain.length === 0) return null;
  const params = paramsFor(chain[chain.length - 1]!.pattern, pathname);

  const labelFor = (route: RouteDef): ReactNode => {
    const dynamic = labels?.[route.pattern];
    if (dynamic) return dynamic;
    if (route.labelKey) return t(route.labelKey, route.label);
    if (route.label) return toTitleCase(route.label);
    // A dynamic crumb nobody named: show the address segment rather than nothing.
    return segments(hrefFor(route.pattern, params)).at(-1);
  };

  // The dashboard is drawn as the home glyph, not as a word.
  const crumbs = chain.filter((route) => route.pattern !== "/");
  const leaf = crumbs[crumbs.length - 1];
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2]! : byPattern.get("/")!;
  const parentHref = hrefFor(parent.pattern, params);
  const parentLabel = labelFor(parent);

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb" data-print="hide">
      {/* The way back. One click, always to the parent register — not to whatever
          the browser saw last, which after a save-and-redirect is often this very
          page again. */}
      <Link
        to={parentHref}
        className="crumb-back"
        title={`Back to ${typeof parentLabel === "string" ? parentLabel.toLowerCase() : "the previous page"}`}
      >
        <CornerUpLeft size={13} aria-hidden />
        <span className="sr-only">Back to {parentLabel}</span>
      </Link>

      <ol className="crumb-trail">
        <li className="crumb-item">
          <Link to="/" className="crumb crumb-home" title={t("nav.dashboard", "Dashboard")}>
            <Home size={13} aria-hidden />
            <span className="sr-only">{t("nav.dashboard", "Dashboard")}</span>
          </Link>
        </li>
        {crumbs.map((route) => {
          const isLeaf = route === leaf;
          return (
            <li key={route.pattern} className="crumb-item">
              <ChevronRight size={12} className="crumb-sep" aria-hidden />
              {isLeaf ? (
                <span className="crumb crumb-current" aria-current="page">
                  {current ?? labelFor(route)}
                </span>
              ) : (
                <Link to={hrefFor(route.pattern, params)} className="crumb">
                  {labelFor(route)}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
